import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyContextService } from '../services/general/currency-context.service';

/**
 * A money figure in a NAMED currency — the pipe to use for a row that carries its own book.
 *
 * It already took the code explicitly, so it never mislabelled anything. What it did share with
 * {@see DefaultCurrencyPipe} is printing a NULL amount as "0.00": `Number(null).toFixed(2)` is "0.00", and
 * `null == 0` is false in JavaScript, so a missing figure slipped past the dashes check and came out as a
 * confident zero — "nothing owed" where the truth is "we do not know". Once ZiG is on the API deliberately
 * returns `null` for a line it cannot price in the book it is settled in (decision D2), so this is reachable
 * by design, not only by a half-loaded screen.
 */
@Pipe({
  name: 'customCurrency',
})
export class CustomCurrencyPipe implements PipeTransform {
  transform(
    value: number | null | undefined,
    currency: string,
    compact: boolean = false,
    showDashes: boolean = false
  ): string {
    // UNKNOWN is not ZERO. Checked first, and deliberately not with `== 0`.
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return CurrencyContextService.UNAVAILABLE;
    }

    if (value == 0 && showDashes) {
      return '--';
    }

    if (compact) {
      let formatter = Intl.NumberFormat('en', {
        notation: 'compact',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return formatter.format(Number(value)) + ' ' + currency;
    }

    // Check if the value is negative
    const isNegative = Number(value) < 0;

    // Remove the negative sign
    const absValue = Math.abs(Number(value));

    // Format the absolute value with commas and fixed decimals
    const formattedValue = absValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

    // Add parentheses for negative values
    const result = isNegative ? '(' + formattedValue + ')' : formattedValue;

    return result + ' ' + currency;
  }
}
